import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import QrScanner from '@agicash/qr-scanner';
import { useTranslation } from 'react-i18next';
import {
  extractFanFromQrPayload,
  getFanQrRegexPatterns,
} from '../services/fanQrParser';
import { isMobilePhone } from '../services/deviceUtils';

const WORKER_URL = `${process.env.PUBLIC_URL || ''}/qr-scanner-worker.js`;

const SCANNER_COPY = {
  scan_fan_qr_title: 'Scan your FAN/FCN QR code',
  scan_fan_qr_close: 'Close',
  scan_fan_qr_hint: 'Align the QR code within the frame.',
  scan_fan_qr_scanning_hint: 'Looking for a FAN or FCN QR code…',
  scan_fan_qr_allow_camera: 'Allow camera',
  scan_fan_qr_retry: 'Try again',
  scan_fan_qr_torch_on: 'Turn flash on',
  scan_fan_qr_torch_off: 'Turn flash off',
  fan_qr_camera_prompt_title: 'Camera access is required',
  fan_qr_camera_prompt_message:
    'Allow camera access to scan the QR code on your Fayda digital ID.',
  fan_qr_camera_denied_title: 'Camera access was blocked',
  fan_qr_camera_denied_message:
    'Allow camera access in your browser settings, then try again.',
  fan_qr_camera_insecure_message:
    'Open this page over a secure connection to use the camera.',
  fan_qr_camera_error: 'The camera could not be started. Try again.',
  fan_qr_invalid: 'This QR code does not contain a valid FAN or FCN.',
};

const WASM_BASE = `${process.env.PUBLIC_URL || ''}/qr-scanner/`;

let workerConfigured = false;

const ensureWorkerConfigured = () => {
  if (!workerConfigured) {
    QrScanner.setWorkerUrl(WORKER_URL);
    QrScanner.configureWasm({
      locateFile: (file) =>
        String(file).endsWith('.wasm') ? `${WASM_BASE}${file}` : file,
    });
    workerConfigured = true;
  }
};

const scanPayload = (result) => {
  if (typeof result === 'string') return result;
  return result?.data || result?.text || result?.rawValue || '';
};

const isNoCodeError = (decodeError) => {
  const text = String(decodeError?.message || decodeError || '');
  if (!text) return true;
  return /no qr code found|not found|no multiformat|no barcode|checksum/i.test(
    text
  );
};

const getPreferredCameraFacingMode = () =>
  isMobilePhone() ? 'environment' : 'user';

const getErrorText = (error) =>
  String(error?.message || error?.name || error || '').toLowerCase();

const isCameraPermissionDenied = (error) => {
  const text = getErrorText(error);

  return (
    text.includes('notallowederror') ||
    text.includes('permissiondeniederror') ||
    text.includes('permission denied') ||
    text.includes('not allowed') ||
    text.includes('denied') ||
    text.includes('camerapermissionerror')
  );
};

const isInsecureCameraError = (error) => {
  const text = getErrorText(error);

  return (
    !window.isSecureContext ||
    text.includes('secure context') ||
    text.includes('only supported in secure')
  );
};

const CameraOutlineIcon = ({ size = 40, className = '' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
    <circle cx="12" cy="13" r="3" />
  </svg>
);

export function requestFanCamera() {
  if (
    typeof navigator === 'undefined' ||
    !window.isSecureContext ||
    !navigator.mediaDevices?.getUserMedia
  ) {
    return null;
  }
  const facing = isMobilePhone() ? 'environment' : 'user';
  try {
    return navigator.mediaDevices.getUserMedia({
      audio: false,
      video: { facingMode: { ideal: facing } },
    });
  } catch (err) {
    return Promise.reject(err);
  }
}

export default function FanQrScanner({
  open,
  onClose,
  onScan,
  openIDConnectService,
  cameraRequest = null,
  i18nKeyPrefix = 'otp',
}) {
  const { t } = useTranslation('translation', { keyPrefix: i18nKeyPrefix });
  const scannerText = (key) =>
    t(key, { defaultValue: SCANNER_COPY[key] || key });
  const scannerRef = useRef(null);
  const streamRef = useRef(null);
  const videoRef = useRef(null);
  const handledRef = useRef(false);
  const sessionRef = useRef(0);
  const nativeScanStopRef = useRef(() => {});
  const onScanRef = useRef(onScan);
  const [error, setError] = useState('');
  const [scannerPhase, setScannerPhase] = useState('prompt');
  const [torchAvailable, setTorchAvailable] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [seenCameraRequest, setSeenCameraRequest] = useState(null);

  onScanRef.current = onScan;

  // The camera is already requested from the Scan QR click. Show the live
  // viewfinder on the first paint instead of an extra Allow camera step.
  if (open && cameraRequest && cameraRequest !== seenCameraRequest) {
    setSeenCameraRequest(cameraRequest);
    setScannerPhase('scanning');
    setError('');
    setTorchAvailable(false);
    setTorchOn(false);
  } else if (!open && seenCameraRequest) {
    setSeenCameraRequest(null);
    setScannerPhase('prompt');
    setError('');
  }

  const stopOwnedStream = useCallback(() => {
    const stream = streamRef.current;
    streamRef.current = null;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const stopScanner = useCallback(() => {
    handledRef.current = true;
    nativeScanStopRef.current();
    nativeScanStopRef.current = () => {};
    stopOwnedStream();
    const activeScanner = scannerRef.current;
    scannerRef.current = null;
    if (activeScanner) {
      activeScanner.destroy();
    }
  }, [stopOwnedStream]);

  const acceptScanResult = useCallback(
    (result) => {
      if (handledRef.current) {
        return;
      }

      const fan = extractFanFromQrPayload(
        scanPayload(result),
        getFanQrRegexPatterns(openIDConnectService)
      );
      if (!fan) {
        setError(scannerText('fan_qr_invalid'));
        return;
      }

      handledRef.current = true;
      nativeScanStopRef.current();
      stopOwnedStream();
      const activeScanner = scannerRef.current;
      scannerRef.current = null;
      if (activeScanner) {
        activeScanner.stop();
      }
      onScanRef.current(fan);
    },
    [openIDConnectService, scannerText, stopOwnedStream]
  );

  const startDecodeLoop = (video) => {
    nativeScanStopRef.current();
    let stopped = false;
    let timer = 0;
    let busy = false;
    let detector = null;
    if (typeof window.BarcodeDetector === 'function') {
      try {
        detector = new window.BarcodeDetector({ formats: ['qr_code'] });
      } catch {
        detector = null;
      }
    }
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });

    const tick = async () => {
      if (stopped || handledRef.current || busy) {
        return;
      }
      if (
        video.readyState >= 2 &&
        video.videoWidth > 0 &&
        video.videoHeight > 0
      ) {
        busy = true;
        try {
          if (detector) {
            const codes = await detector.detect(video);
            const raw = codes?.[0]?.rawValue;
            if (raw) {
              acceptScanResult({ data: raw });
            }
          }
          if (!handledRef.current && ctx) {
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            ctx.drawImage(video, 0, 0);
            const result = await QrScanner.scanImage(canvas, {
              decoderOptions: {
                tryHarder: true,
                tryInvert: true,
                tryRotate: true,
                formats: ['QRCode'],
              },
            });
            acceptScanResult(result);
          }
        } catch (decodeError) {
          if (!isNoCodeError(decodeError)) {
            console.error('[FAN QR] decode failed:', decodeError);
          }
        } finally {
          busy = false;
        }
      }
      if (!stopped && !handledRef.current) {
        timer = window.setTimeout(tick, 280);
      }
    };

    timer = window.setTimeout(tick, 200);
    nativeScanStopRef.current = () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  };

  const scannerTextRef = useRef(scannerText);
  scannerTextRef.current = scannerText;
  const startDecodeLoopRef = useRef(startDecodeLoop);
  startDecodeLoopRef.current = startDecodeLoop;

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const session = ++sessionRef.current;
    handledRef.current = false;
    ensureWorkerConfigured();
    QrScanner.preload().catch(() => {});
    setError('');
    setTorchAvailable(false);
    setTorchOn(false);

    const streamRequest = cameraRequest;
    if (!streamRequest) {
      setScannerPhase(window.isSecureContext ? 'error' : 'insecure');
      if (window.isSecureContext) {
        setError(scannerTextRef.current('fan_qr_camera_error'));
      }
    } else {
      setScannerPhase('scanning');
      streamRequest
        .then(async (stream) => {
          if (handledRef.current || sessionRef.current !== session) {
            return;
          }
          const video = videoRef.current;
          if (!video) {
            return;
          }
          streamRef.current = stream;
          video.muted = true;
          video.setAttribute('playsinline', 'true');
          video.srcObject = stream;
          await video.play();
          if (handledRef.current || sessionRef.current !== session) {
            return;
          }
          const track = stream.getVideoTracks()[0];
          const caps = track?.getCapabilities?.() || {};
          setTorchAvailable(Boolean(caps.torch));
          startDecodeLoopRef.current(video);
        })
        .catch((err) => {
          if (handledRef.current || sessionRef.current !== session) return;
          if (isInsecureCameraError(err)) {
            setScannerPhase('insecure');
          } else if (isCameraPermissionDenied(err)) {
            setScannerPhase('denied');
          } else {
            setError(scannerTextRef.current('fan_qr_camera_error'));
            setScannerPhase('error');
          }
        });
    }

    return () => {
      window.setTimeout(() => {
        if (sessionRef.current === session) {
          stopScanner();
        }
      }, 0);
    };
  }, [open, cameraRequest, stopScanner]);

  const handleAllowCamera = () => {
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      setScannerPhase('insecure');
      return;
    }

    const video = videoRef.current;
    if (!video) {
      return;
    }

    const facing = getPreferredCameraFacingMode();
    // getUserMedia has to be requested in this click, before any await,
    // or mobile browsers drop the camera permission.
    let streamRequest;
    try {
      streamRequest = navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: facing } },
      });
    } catch (err) {
      setError(scannerText('fan_qr_camera_error'));
      setScannerPhase('error');
      return;
    }

    setError('');
    setTorchAvailable(false);
    setTorchOn(false);
    setScannerPhase('scanning');
    handledRef.current = false;
    nativeScanStopRef.current();
    nativeScanStopRef.current = () => {};
    stopOwnedStream();
    ensureWorkerConfigured();

    video.muted = true;
    video.setAttribute('playsinline', 'true');

    streamRequest
      .then(async (stream) => {
        if (handledRef.current) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        video.srcObject = stream;
        await video.play();
        const track = stream.getVideoTracks()[0];
        const caps = track?.getCapabilities?.() || {};
        setTorchAvailable(Boolean(caps.torch));
        startDecodeLoop(video);
      })
      .catch((err) => {
        if (handledRef.current) return;
        if (isInsecureCameraError(err)) {
          setScannerPhase('insecure');
        } else if (isCameraPermissionDenied(err)) {
          setScannerPhase('denied');
        } else {
          setError(scannerText('fan_qr_camera_error'));
          setScannerPhase('error');
        }
      });
  };

  const toggleTorch = async () => {
    const track = streamRef.current?.getVideoTracks?.()[0];
    if (!track || !torchAvailable) {
      return;
    }

    try {
      await track.applyConstraints({ advanced: [{ torch: !torchOn }] });
      setTorchOn(!torchOn);
    } catch {
      setTorchAvailable(false);
      setTorchOn(false);
    }
  };

  const showCameraPrompt =
    scannerPhase === 'prompt' ||
    scannerPhase === 'denied' ||
    scannerPhase === 'insecure' ||
    scannerPhase === 'error';

  const promptTitle =
    scannerPhase === 'denied'
      ? scannerText('fan_qr_camera_denied_title')
      : scannerPhase === 'insecure'
        ? scannerText('fan_qr_camera_denied_title')
        : scannerText('fan_qr_camera_prompt_title');

  const promptMessage =
    scannerPhase === 'denied'
      ? scannerText('fan_qr_camera_denied_message')
      : scannerPhase === 'insecure'
        ? scannerText('fan_qr_camera_insecure_message')
        : scannerPhase === 'error'
          ? error || scannerText('fan_qr_camera_error')
          : scannerText('fan_qr_camera_prompt_message');

  const promptButtonLabel =
    scannerPhase === 'prompt'
      ? scannerText('scan_fan_qr_allow_camera')
      : scannerText('scan_fan_qr_retry');

  if (!open) {
    return null;
  }

  const modal = (
    <div
      className="vf-fan-qr-overlay fixed inset-0 z-[10000] flex items-center justify-center bg-black/45 p-2 sm:p-4 backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="fan-qr-scanner-title"
    >
      <div className="vf-fan-qr-dialog vf-ds-modal-card flex max-h-[calc(100dvh-1rem)] w-full max-w-md flex-col overflow-y-auto rounded-xl p-6 text-white shadow-xl sm:max-h-[calc(100dvh-2rem)]">
        <div className="mb-2 flex items-start justify-between gap-2 sm:mb-3 sm:gap-3">
          <h2
            id="fan-qr-scanner-title"
            className="pr-2 text-base font-semibold leading-snug sm:text-lg"
          >
            {scannerText('scan_fan_qr_title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="vf-fan-qr-close shrink-0 rounded-lg px-3 py-1 text-sm bg-white/10 hover:bg-white/20"
            aria-label={scannerText('scan_fan_qr_close')}
          >
            {scannerText('scan_fan_qr_close')}
          </button>
        </div>

        <p className="vf-fan-qr-hint mb-3 text-xs leading-relaxed text-white/80 sm:text-sm">
          {scannerPhase === 'scanning'
            ? scannerText('scan_fan_qr_scanning_hint')
            : scannerText('scan_fan_qr_hint')}
        </p>

        <div className="fan-qr-scanner-viewport relative -mx-3 aspect-video w-[calc(100%+1.5rem)] max-h-[min(72dvh,720px)] overflow-hidden bg-black sm:mx-0 sm:w-full sm:rounded-xl">
          <video
            ref={videoRef}
            className="fan-qr-scanner-video absolute inset-0 h-full w-full"
            muted
            autoPlay
            playsInline
            aria-hidden={showCameraPrompt}
          />
          {scannerPhase === 'scanning' && torchAvailable && (
            <button
              type="button"
              onClick={toggleTorch}
              className="absolute right-2 top-2 z-10 rounded-lg bg-black/50 px-3 py-1.5 text-xs font-medium hover:bg-black/70"
              aria-pressed={torchOn}
            >
              {torchOn
                ? scannerText('scan_fan_qr_torch_off')
                : scannerText('scan_fan_qr_torch_on')}
            </button>
          )}
          {showCameraPrompt && (
            <div className="vf-fan-qr-camera-prompt absolute inset-0 flex flex-col items-center justify-center gap-3 overflow-y-auto bg-[#0a2a36] p-4 text-center sm:gap-4 sm:p-5">
              <CameraOutlineIcon
                size={40}
                className="shrink-0 text-white/90 sm:hidden"
              />
              <CameraOutlineIcon
                size={44}
                className="hidden shrink-0 text-white/90 sm:block"
              />
              <div className="space-y-2">
                <p className="text-sm font-semibold sm:text-base">
                  {promptTitle}
                </p>
                <p className="text-xs leading-relaxed text-white/80 sm:text-sm">
                  {promptMessage}
                </p>
              </div>
              {scannerPhase !== 'insecure' && (
                <button
                  type="button"
                  onClick={handleAllowCamera}
                  className="vf-fan-qr-allow w-full max-w-xs rounded-lg bg-white/15 px-4 py-2.5 text-sm font-medium hover:bg-white/25 sm:w-auto"
                >
                  {promptButtonLabel}
                </button>
              )}
            </div>
          )}
        </div>

        {error && (
          <p
            className="mt-3 text-center text-xs text-red-200 sm:text-sm"
            role="alert"
          >
            {error}
          </p>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
